/**
 * Scroll performance diagnostics. Enable by adding `?perf` to the URL
 * (e.g. https://dev.speedduel.radapplab.com/?perf), then open the browser
 * console and scroll. After each scroll gesture it logs frame timing (fps +
 * janky frames); it also reports any main-thread "long tasks" (>50ms), which
 * are the usual cause of stuttery scrolling.
 */
export function installScrollPerf(): void {
  if (typeof window === 'undefined' || !/[?&]perf\b/.test(window.location.search)) return;

  // eslint-disable-next-line no-console
  console.log(
    `%c[scroll-perf] enabled · dpr=${window.devicePixelRatio} · cores=${navigator.hardwareConcurrency} · ${window.innerWidth}x${window.innerHeight}`,
    'color:#16a6f5;font-weight:bold',
  );

  // Long-task observer — surfaces main-thread blocks (JS, layout, paint bursts).
  try {
    const po = new PerformanceObserver(list => {
      for (const e of list.getEntries()) {
        if (e.duration > 50) {
          // eslint-disable-next-line no-console
          console.warn(`[longtask] ${e.duration.toFixed(0)}ms`, (e as PerformanceEntry & { attribution?: unknown }).attribution ?? e);
        }
      }
    });
    po.observe({ entryTypes: ['longtask'] });
  } catch { /* longtask not supported (e.g. Safari) */ }

  // Per-gesture frame timing.
  let scrolling = false;
  let frames: number[] = [];
  let last = 0;
  let raf = 0;
  let endTimer: ReturnType<typeof setTimeout>;

  const tick = (t: number) => {
    if (last) frames.push(t - last);
    last = t;
    if (scrolling) raf = requestAnimationFrame(tick);
  };

  const onScroll = (e: Event) => {
    if (!scrolling) {
      scrolling = true;
      frames = [];
      last = 0;
      (onScroll as unknown as { target?: EventTarget | null }).target = e.target;
      raf = requestAnimationFrame(tick);
    }
    clearTimeout(endTimer);
    endTimer = setTimeout(() => {
      scrolling = false;
      cancelAnimationFrame(raf);
      if (frames.length < 3) return;
      const avg = frames.reduce((a, b) => a + b, 0) / frames.length;
      const jank = frames.filter(f => f > 24).length;
      const max = Math.round(Math.max(...frames));
      const tgt = (onScroll as unknown as { target?: EventTarget | null }).target;
      const where = tgt instanceof HTMLElement ? (tgt.className || tgt.tagName) : 'window';
      // eslint-disable-next-line no-console
      console.log(
        `[scroll-perf] ${String(where).slice(0, 40)} · frames=${frames.length} avg=${avg.toFixed(1)}ms ~${Math.round(1000 / avg)}fps · jank(>24ms)=${jank} · max=${max}ms`,
      );
    }, 150);
  };

  // capture:true so scrolls inside nested scroll containers are seen too.
  window.addEventListener('scroll', onScroll, { passive: true, capture: true });
}
